import { User } from 'firebase/auth';
import {
  addDoc,
  collection,
  deleteDoc,
  deleteField,
  doc,
  DocumentData,
  getCountFromServer,
  getDoc,
  getFirestore,
  onSnapshot,
  Query,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import _ from 'lodash';
import moment from 'moment';
import {
  AlQuran,
  AlQuranFormData,
  Azkaar,
  AzkaarFormData,
  Campange,
  CampangeFormData,
  Content,
  Cotisation,
  CotisationFormData,
  Countable,
  DateField,
  Filter,
  Khassida,
  KhassidaFormData,
  MessageContact,
  MessageContactFormData,
  Playlist,
  PlaylistFormData,
  Salatou,
  SalatouFormData,
  UserFormData,
  UserInfos
} from '../../types';
import { PATHS } from './api';
import {
  uploadKhassida,
  uploadUserAvatarAsync,
  uploadUserCardMemberAsync
} from './storage.api';

const firestore = getFirestore();
const functions = getFunctions();

export async function createCotisationsync(data: CotisationFormData) {
  const sendData = { ...data, date: getDate(moment()) };

  return addDoc(collection(firestore, PATHS.cotisations), sendData);
}

export async function deleteCotisation(uid: string) {
  return deleteDoc(doc(firestore, PATHS.cotisations, uid));
}

export async function updateCotisationAsync(
  uid: string,
  data: CotisationFormData
) {
  return updateDoc(doc(firestore, PATHS.cotisations, uid), data as any);
}

export async function createAlQuranAsync(data: AlQuranFormData) {
  const sendData = { ...data, created_at: serverTimestamp() };

  return addDoc(collection(firestore, PATHS.AlQuran), sendData);
}

export async function deleteAlQuran(uid: string) {
  return deleteDoc(doc(firestore, PATHS.AlQuran, uid));
}

export async function updateAlQuranAsync(uid: string, data: AlQuranFormData) {
  return updateDoc(doc(firestore, PATHS.AlQuran, uid), data as any);
}

export async function createKhassidaAsync(data: KhassidaFormData) {
  const sendData = { ...data, created_at: serverTimestamp() };

  return addDoc(collection(firestore, PATHS.khassida), sendData);
}

export async function deleteKhassida(uid: string) {
  return deleteDoc(doc(firestore, PATHS.khassida, uid));
}

export async function deleteCountable(uid: string) {
  return deleteDoc(doc(firestore, PATHS.countables, uid));
}

export async function updateKhassidaAsync(uid: string, data: KhassidaFormData) {
  return updateDoc(doc(firestore, PATHS.khassida, uid), data as any);
}

export async function createAzkaarAsync(data: AzkaarFormData) {
  const sendData = { ...data, created_at: serverTimestamp() };

  return addDoc(collection(firestore, PATHS.azkaar), sendData);
}

export async function deleteAzkaar(uid: string) {
  return deleteDoc(doc(firestore, PATHS.azkaar, uid));
}

export async function updateAzkaarAsync(uid: string, data: AzkaarFormData) {
  return updateDoc(doc(firestore, PATHS.azkaar, uid), data as any);
}

export async function createSalatouAsync(data: SalatouFormData) {
  const sendData = { ...data, created_at: serverTimestamp() };

  return addDoc(collection(firestore, PATHS.salatous), sendData);
}

export async function deleteSalatou(uid: string) {
  return deleteDoc(doc(firestore, PATHS.salatous, uid));
}

export async function updateSalatouAsync(uid: string, data: SalatouFormData) {
  return updateDoc(doc(firestore, PATHS.salatous, uid), data as any);
}

export async function createCampangeAsync(data: CampangeFormData) {
  const sendData = { ...data, created_at: serverTimestamp() };

  return addDoc(collection(firestore, PATHS.campanges), sendData);
}

export async function deleteCampange(uid: string) {
  return deleteDoc(doc(firestore, PATHS.campanges, uid));
}

export async function updateCampangeAsync(uid: string, data: CampangeFormData) {
  return updateDoc(doc(firestore, PATHS.campanges, uid), data as any);
}

export async function createPlaylistAsync({
  audio,
  ...data
}: PlaylistFormData) {
  const sendData = { ...data, created_at: serverTimestamp() };
  if (audio && audio instanceof File) {
    sendData.link = await uploadKhassida(audio);
  }
  return addDoc(collection(firestore, PATHS.playlists), sendData);
}

export async function deletePlaylist(uid: string) {
  return deleteDoc(doc(firestore, PATHS.playlists, uid));
}

export async function updatePlaylistAsync(
  uid: string,
  { audio, ...data }: PlaylistFormData
) {
  const sendData = { ...data };
  if (audio && audio instanceof File) {
    sendData.link = await uploadKhassida(audio);
  }
  return updateDoc(doc(firestore, PATHS.playlists, uid), data as any);
}

export async function createUserAsync(user: User, data: UserFormData) {
  const sendData = _.omit(data, ['password', 'confirmPassword']);
  sendData.created_at = serverTimestamp();
  return setDoc(doc(firestore, PATHS.users, user?.uid), sendData);
}

export async function updateUserAsync(uid: string, data: UserFormData) {
  return updateDoc(doc(firestore, PATHS.users, uid), data);
}

export async function getUserByUid(uid: string) {
  const docRef = await getDoc(doc(firestore, PATHS.users, uid));
  if (docRef.exists()) {
    return getDocData(docRef) as UserInfos;
  }
  return null;
}

export async function updateUserAvatarAsync(uid: string, avatar: File) {
  const uploaded = await uploadUserAvatarAsync(avatar, uid);
  if (uploaded) {
    return updateUserAsync(uid, { avatar: uploaded });
  }
  return null;
}

export async function updateUserCardMemberAsync(
  uid: string,
  cardMember: Blob,
  additionalData?: UserFormData
) {
  const uploaded = await uploadUserCardMemberAsync(cardMember, uid);
  if (uploaded) {
    return updateUserAsync(uid, {
      cardMember: uploaded,
      ...(additionalData ? additionalData : {})
    });
  }
  return null;
}

export async function deleteUserToken(uid?: string) {
  if (!uid) return;

  updateDoc(doc(firestore, PATHS.users, uid), { token: deleteField() });
}

export function userListener(
  userId: string,
  cb: (dzta: UserInfos | null) => any
) {
  return onSnapshot(doc(firestore, PATHS.users, userId), async (snapshot) => {
    if (snapshot.exists()) {
      cb(getDocData<UserInfos>(snapshot));
    } else {
      cb(null);
    }
  });
}

export function contentListner(cb: (dzta: Content) => any) {
  return onSnapshot(
    doc(firestore, PATHS.contents, 'contents'),
    async (snapshot) => {
      if (snapshot.exists()) {
        cb(getDocData<Content>(snapshot));
      }
    }
  );
}

export const getDate = (currentDate: moment.Moment) => {
  const date: DateField = {
    date: currentDate.toDate().getTime(),
    day: currentDate.format('DD/MM/YYYY'),
    month: currentDate.format('MM/YYYY'),
    week: currentDate.startOf('week').format('DD/MM/YYYY'),
    year: currentDate.year()
  };

  return date;
};

export type Conds<T = Countable> = { field: keyof T; value: string | boolean };

export function countableListener(
  cb: (data: Countable[], removeds: string[]) => any,
  filter: Filter,
  startDate?: string,
  endDate?: string,
  conds?: Conds,
  campagne?: string
) {
  let q: Query<DocumentData>;
  if (campagne) {
    q = query(
      collection(firestore, PATHS.countables),
      where(`campagne`, '==', campagne)
    );
    if (conds) {
      q = query(
        collection(firestore, PATHS.countables),
        where(`campagne`, '==', campagne),
        where(conds.field, '==', conds.value)
      );
    }
  } else {
    if (filter === 'date_between') {
      const start = moment(startDate).toDate().getTime();
      const end = moment(endDate).toDate().getTime();
      q = query(
        collection(firestore, PATHS.countables),
        where(`date.date`, '>=', start),
        where(`date.date`, '<=', end)
      );

      if (conds) {
        q = query(
          collection(firestore, PATHS.countables),
          where(`date.date`, '>=', start),
          where(`date.date`, '<=', end),
          where(conds.field, '==', conds.value)
        );
      }
    } else {
      let date = getDate(moment(startDate));
      let field: keyof DateField = 'week';
      if (filter === 'last_week') {
        date = getDate(moment().subtract(1, 'week'));
      } else if (filter === 'last_month') {
        field = 'month';
        date = getDate(moment().subtract(1, 'month'));
      } else if (filter === 'month') {
        field = 'month';
      } else if (filter === 'today') {
        field = 'day';
      }
      q = query(
        collection(firestore, PATHS.countables),
        where(`date.${field}`, '==', date[field])
      );

      if (conds) {
        q = query(
          collection(firestore, PATHS.countables),
          where(`date.${field}`, '==', date[field]),
          where(conds.field, '==', conds.value)
        );
      }
    }
  }

  return onSnapshot(q, async (querySnapshot) => {
    const data: Countable[] = [];
    const removeds: string[] = [];
    querySnapshot.docChanges().forEach((change) => {
      if (change.type !== 'removed') {
        const dta = getDocData<Countable>(change.doc);
        data.push(dta);
      } else {
        removeds.push(change.doc.id);
      }
    });
    cb(data, removeds);
  });
}

export function cotisationListener(
  cb: (data: Cotisation[], deleteds: string[]) => any,
  filter: Filter,
  startDate?: string,
  endDate?: string,
  conds?: Conds<Cotisation>
) {
  let q: any = collection(firestore, PATHS.cotisations);
  if (filter) {
    if (filter === 'date_between') {
      const start = moment(startDate).toDate().getTime();
      const end = moment(endDate).toDate().getTime();
      q = query(
        collection(firestore, PATHS.cotisations),
        where(`date.date`, '>=', start),
        where(`date.date`, '<=', end)
      );
      if (conds) {
        q = query(
          collection(firestore, PATHS.cotisations),
          where(`date.date`, '>=', start),
          where(`date.date`, '<=', end),
          where(conds.field, '==', conds.value)
        );
      }
    } else {
      let date = getDate(moment(startDate));
      let field: keyof DateField = 'week';
      if (filter === 'last_week') {
        date = getDate(moment().subtract(1, 'week'));
      } else if (filter === 'last_month') {
        field = 'month';
        date = getDate(moment().subtract(1, 'month'));
      } else if (filter === 'month') {
        field = 'month';
      } else if (filter === 'today') {
        field = 'day';
      }

      q = query(
        collection(firestore, PATHS.cotisations),
        where(`date.${field}`, '==', date[field])
      );
      if (conds) {
        q = query(
          collection(firestore, PATHS.cotisations),
          where(`date.${field}`, '==', date[field]),
          where(conds.field, '==', conds.value)
        );
      }
    }
  }

  return onSnapshot(q, async (querySnapshot) => {
    const data: Cotisation[] = [];
    const deleteds: string[] = [];
    querySnapshot.docChanges().forEach((change) => {
      if (change.type !== 'removed') {
        const dta = getDocData<Cotisation>(change.doc);
        data.push(dta);
      } else {
        deleteds.push(change.doc.id);
      }
    });
    cb(data, deleteds);
  });
}

export async function getUsersCount() {
  return (await getCountFromServer(collection(firestore, PATHS.users)))?.data()
    ?.count;
}

export function usersListener(
  cb: (data: UserInfos[], deleteds: string[]) => any
) {
  return onSnapshot(
    collection(firestore, PATHS.users),
    async (querySnapshot) => {
      const data: UserInfos[] = [];
      const deleteds: string[] = [];
      querySnapshot.docChanges().forEach((change) => {
        if (change.type === 'removed') {
          deleteds.push(change.doc.id);
        } else {
          const dta = getDocData<UserInfos>(change.doc);
          data.push(dta as UserInfos);
        }
      });
      cb(data, deleteds);
    }
  );
}

export function contactsListener(
  cb: (data: MessageContact[], deleteds: string[]) => any
) {
  return onSnapshot(
    collection(firestore, PATHS.contacts),
    async (querySnapshot) => {
      const data: MessageContact[] = [];
      const deleteds: string[] = [];
      querySnapshot.docChanges().forEach((change) => {
        if (change.type === 'removed') {
          deleteds.push(change.doc.id);
        } else {
          const dta = getDocData<MessageContact>(change.doc);
          data.push(dta as MessageContact);
        }
      });
      cb(data, deleteds);
    }
  );
}

export function khassidaListener(
  cb: (data: Khassida[], deleteds: string[]) => any
) {
  return onSnapshot(
    collection(firestore, PATHS.khassida),
    async (querySnapshot) => {
      const data: Khassida[] = [];
      const deleteds: string[] = [];
      querySnapshot.docChanges().forEach((change) => {
        if (change.type === 'removed') {
          deleteds.push(change.doc.id);
        } else {
          const dta = getDocData<Khassida>(change.doc);
          data.push(dta);
        }
      });
      cb(data, deleteds);
    }
  );
}

export function salatousListener(
  cb: (data: Salatou[], deleteds: string[]) => any
) {
  return onSnapshot(
    collection(firestore, PATHS.salatous),
    async (querySnapshot) => {
      const data: Salatou[] = [];
      const deleteds: string[] = [];
      querySnapshot.docChanges().forEach((change) => {
        if (change.type === 'removed') {
          deleteds.push(change.doc.id);
        } else {
          const dta = getDocData<Salatou>(change.doc);
          data.push(dta);
        }
      });
      cb(data, deleteds);
    }
  );
}

export function campangeListener(
  cb: (data: Campange[], deleteds: string[]) => any
) {
  return onSnapshot(
    collection(firestore, PATHS.campanges),
    async (querySnapshot) => {
      const data: Campange[] = [];
      const deleteds: string[] = [];
      querySnapshot.docChanges().forEach((change) => {
        if (change.type === 'removed') {
          deleteds.push(change.doc.id);
        } else {
          const dta = getDocData<Campange>(change.doc);
          data.push(dta);
        }
      });
      cb(data, deleteds);
    }
  );
}

export function playlistListener(
  cb: (data: Playlist[], deleteds: string[]) => any
) {
  return onSnapshot(
    collection(firestore, PATHS.playlists),
    async (querySnapshot) => {
      const data: Playlist[] = [];
      const deleteds: string[] = [];
      querySnapshot.docChanges().forEach((change) => {
        if (change.type === 'removed') {
          deleteds.push(change.doc.id);
        } else {
          const dta = getDocData<Playlist>(change.doc);
          data.push(dta);
        }
      });
      cb(data, deleteds);
    }
  );
}

export function azkaarListener(
  cb: (data: Azkaar[], deleteds: string[]) => any
) {
  return onSnapshot(
    collection(firestore, PATHS.azkaar),
    async (querySnapshot) => {
      const data: Azkaar[] = [];
      const deleteds: string[] = [];
      querySnapshot.docChanges().forEach((change) => {
        if (change.type === 'removed') {
          deleteds.push(change.doc.id);
        } else {
          const dta = getDocData<Azkaar>(change.doc);
          data.push(dta);
        }
      });
      cb(data, deleteds);
    }
  );
}

export function alQuranListener(
  cb: (data: AlQuran[], deleteds: string[]) => any
) {
  return onSnapshot(
    collection(firestore, PATHS.AlQuran),
    async (querySnapshot) => {
      const data: AlQuran[] = [];
      const deleteds: string[] = [];
      querySnapshot.docChanges().forEach((change) => {
        if (change.type === 'removed') {
          deleteds.push(change.doc.id);
        } else {
          const dta = getDocData<AlQuran>(change.doc);
          data.push(dta);
        }
      });
      cb(data, deleteds);
    }
  );
}

export function createContactMessage(data: MessageContactFormData) {
  const sendData = {
    ...data,
    created_at: serverTimestamp()
  };
  const doc = collection(firestore, PATHS.contacts);
  return addDoc(doc, sendData);
}

export function deleteNormalUserFunction(id: string) {
  const callable = httpsCallable(functions, 'deleteNormalUser');
  return callable(id);
}

export function addAdminFunction(data: UserFormData) {
  const callable = httpsCallable(functions, 'createAdmin');
  return callable(data);
}

export async function updateCgu(content: string, type: keyof Content) {
  return setDoc(
    doc(firestore, PATHS.contents, 'contents'),
    { [type]: content },
    { merge: true }
  );
}

type SendResponseMail = {
  email: string;
  subject: string;
  response: string;
};

export function sendResponseContactFunction(data: SendResponseMail) {
  const callable = httpsCallable(functions, 'sendResponseContact');
  return callable(data);
}

export async function responseMessageContact(id: string, response: string) {
  const item = await getDoc(doc(firestore, PATHS.contacts, id));
  if (item.exists()) {
    const data = item.data() as any;
    const responses = data.responses
      ? [...data.responses, response]
      : [response];
    await updateMessageContact(id, { responses });
    sendResponseContactFunction({
      email: data.email,
      response: response,
      subject: data.subject ?? data.message
    });
  }
  return null;
}
export async function updateMessageContact(
  id: string,
  data: MessageContactFormData
) {
  return updateDoc(doc(firestore, PATHS.contacts, id), data);
}

export function toggleUserStatus(userId: string) {
  const callable = httpsCallable(functions, 'toggleUserStatus');
  return callable(userId);
}

export function deleteUserFunction(id: string) {
  const callable = httpsCallable(functions, 'deleteUser');
  return callable(id);
}

export function getDocData<T>(doc: DocumentData) {
  const data = doc.data();
  return {
    ...data,
    created_at: moment(data.created_at?.toDate()).toDate().getTime(),
    uid: doc.id,
    id: doc.id
  } as T;
}
